import React, { useMemo } from 'react';
import classNames from 'classnames';
import { useIntl } from 'umi';

import {
  formatYamlTestFailuresText,
  type TYamlTestFormatMessage,
} from '@/pages/datalab/pages/Number/ModelManagementCenter/utils/showYamlTestFailureFeedback';

import type { IYamlWorkflowRowState } from '../../../types';
import {
  formatSampleQueryResultText,
  type TFormatSampleQueryMessage,
} from '../utils/formatSampleQueryResult';

interface IResultRowsProps {
  rows: IYamlWorkflowRowState[];
}

type TRowMetaKey = 'yaml' | 'sample';

/**
 * YAML / Sample 两行测试结果（图标 + 标题 + 描述 + 失败/成功详情）。
 */
const ResultRows: React.FC<IResultRowsProps> = ({ rows }) => {
  const { formatMessage } = useIntl();
  const formatYamlTest =
    formatMessage as unknown as TYamlTestFormatMessage;

  const rowMeta = useMemo(
    (): Record<TRowMetaKey, { title: string; desc: string; }> => ({
      yaml: {
        title: formatMessage({ id: 'dbl_model_agent_step4_test_yaml_title' }),
        desc: formatMessage({ id: 'dbl_model_agent_step4_test_yaml_desc' }),
      },
      sample: {
        title: formatMessage({ id: 'dbl_model_agent_step4_test_sample_title' }),
        desc: formatMessage({ id: 'dbl_model_agent_step4_test_sample_desc' }),
      },
    }),
    [formatMessage],
  );

  return (
    <div className="dbl-model-agent-create-step-test-publish__test-result">
      {rows.map((row) => {
        const meta = rowMeta[row.key];
        let iconClass = 'is-warn';
        let iconChar = '⏳';
        if (row.status === 'pass') {
          iconClass = 'is-pass';
          iconChar = '\u2714';
        } else if (row.status === 'fail') {
          iconClass = 'is-fail';
          iconChar = '\u26A0';
        }
        let descText = meta.desc;
        if (row.status === 'running') {
          descText = formatMessage(
            { id: 'dbl_model_agent_step4_test_running_line' },
            { name: meta.title },
          );
        } else if (row.status === 'pass') {
          descText = formatMessage(
            { id: 'dbl_model_agent_step4_test_pass_line' },
            { name: meta.title },
          );
        } else if (row.status === 'fail') {
          descText = formatMessage({
            id: 'dbl_model_management_test_failed',
          });
        }
        const failDetailText =
          row.status === 'fail' && row.failures?.length
            ? formatYamlTestFailuresText(formatYamlTest, row.failures)
            : '';
        const passDetailText =
          row.key === 'sample' &&
          row.status === 'pass' &&
          row.success
            ? formatSampleQueryResultText(
              row.success,
              formatMessage as TFormatSampleQueryMessage,
            )
            : '';

        return (
          <div
            key={row.key}
            className="dbl-model-agent-create-step-test-publish__test-row"
          >
            <span
              className={classNames(
                'dbl-model-agent-create-step-test-publish__test-icon',
                iconClass,
              )}
              aria-hidden
            >
              {iconChar}
            </span>
            <div className="dbl-model-agent-create-step-test-publish__test-body">
              <div className="dbl-model-agent-create-step-test-publish__test-title">
                {meta.title}
              </div>
              <div className="dbl-model-agent-create-step-test-publish__test-desc">
                {descText}
              </div>
              {failDetailText ? (
                <pre
                  className={
                    'dbl-model-agent-create-step-test-publish__test-fail-detail'
                  }
                >
                  {failDetailText}
                </pre>
              ) : null}
              {passDetailText ? (
                <pre
                  className={
                    'dbl-model-agent-create-step-test-publish__test-pass-detail'
                  }
                >
                  {passDetailText}
                </pre>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ResultRows;
